import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { SingleSpaContext } from 'single-spa-react';
import ls from 'local-storage';
import { navigateToUrl, getMountedApps } from 'single-spa';
import { SideNav, TopHeader, useBreakpoint } from 'payu-ui/v2';
import {
  isTestMode,
  toggleMerchantSession,
  getCurrentSession,
  isConsolidatedDashboard,
  getConsolidatedUsersDetails,
  getSelectedConsolidatedMids,
  setSelectedConsolidatedMids,
  isConsolidatedDashboardEnabled,
} from "dashboard-utils";
import { getPageName } from "dashboard-utils/lib/constants";
import CleverTapHandler, { event } from "dashboard-utils/lib/utils/clevertap";
import { SideMenu } from "./SideMenu";
import { VistaSideMenu } from "./VistaSideMenu";
import { FeatureSideMenu } from "./FeatureSideMenu";
import { fetchTokenInfo, fetchMerchantParams } from "./services/api/token_info";
import {
  fetchMerchantInfo,
  isOfferMerchant,
} from './services/api/merchant_info';
import {
  isVistaApplication,
  getFlagValueFromAttachConfig,
  getFeatureSideMenuSections,
} from "./utils/vistaApp";
import { MERCHANT_SESSION } from "./constants/env";
import { appUrls, endPoints } from "./constants/urls";
import { migrateToV1 } from "dashboard-utils/lib/utils/migration/merchant_details";
import Portal from "./components/Portal";
import { getFinalSection, parseForFlags } from "./utils/common";
import {
  getMenus,
  getLoginToBizForm,
  getMidSwitcherOptions,
} from './utils/topbar';
import { MENU_ITEM_IDENTIFIERS } from './constants/global';
import './index.scss';
import {
  MERCHANT_MID,
  TEST_MERCHANT_MID,
  AUTH_TOKEN,
  TEST_AUTH_TOKEN,
} from './constants/cookieKeys';
import Cookies from 'universal-cookie';
import { getUserScopes } from './utils/userScopes';
import clsx from 'clsx';
import PayuFinModal from './components/PayuFinModal';
import { isAdminUser } from 'Utils/userScopes';
import useCleverTapEventOnRouteChange from './components/useCleverTapEventOnRouteChange';
import SwitchModes from './components/SwitchModes';
import {
  downTimeListActions,
  downTimeListSelectors,
} from 'Store/ducks/downtime';
import { getActiveDowntime } from 'Constants/downtime';
import { connect } from 'react-redux';

const cookies = new Cookies();
const merchantMID = isTestMode
  ? cookies.get(TEST_MERCHANT_MID)
  : cookies.get(MERCHANT_MID);
const authToken = isTestMode
  ? cookies.get(TEST_AUTH_TOKEN)
  : cookies.get(AUTH_TOKEN);
const logoutUrl = `${process.env.ONBOARDING_HOST}${endPoints.CO_LOGOUT_REDIRECT}${appUrls.DASHBOARD_HOST}`;

const updateSwitcherMenuWithEvent = (
  DASHBOAR_SWITCHER_MENU,
  onboardingComplete
) => {
  DASHBOAR_SWITCHER_MENU.dropdownOptions.forEach((option) => {
    // append event function alongside handleClick function and register onClick on dropdown option
    let clickFn = option?.handleClick;
    option.onClick = (...params) => {
      event('Dashboard CTA clicked', {
        'Event name': 'Dashboard CTA clicked',
        'CTA page': `${getPageName(
          window?.location?.pathname?.split('/')?.pop(),
          onboardingComplete
        )}`,
        'CTA name': 'Switch Dashboard',
        'Section name': 'Header',
        'CTA type': 'View',
        Dashboard_category: `${option.label}`,
      });
      clickFn(...params);
    };
  });
};

const App = (props) => {
  const { downtimeList, fetchDowntime } = props;

  const selectedMidsRef = useRef([]);
  const [userScopes, setUserScopes] = useState([]);
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [merchantParams, setMerchantParams] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [sideMenus, setSideMenus] = useState(SideMenu);
  const [activeApp, setActiveApp] = useState('');
  const [topbarMenus, setTopbarMenus] = useState(getMenus());
  const [forceShowTabs, setForceShowTabs] = useState([]);
  const [showMSideMenu, setShowMSideMenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [merchantSession, setMerchantSession] = useState(
    getCurrentSession() || MERCHANT_SESSION.LIVE
  );
  const [openPopup, setOpenPopup] = useState(false);
  const [uuid, setUuid] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [infoResponse, setInfoResponse] = useState();
  const [showSwitchModeModal, setShowSwitchModeModal] = useState(false);
  const history = useHistory();
  //Keeping this toggle at the tablet breakpoint
  const isMobile = useBreakpoint('m');
  const isTablet = useBreakpoint('t');
  useCleverTapEventOnRouteChange();
  // Active Downtime Notification Data
  const activeDowntimeData = getActiveDowntime(downtimeList?.data);
  const [isActiveDowntime, setIsActiveDowntime] = useState(false);
  const intervalIdRef = useRef(null);

  const showSwitchMid = useMemo(() => {
    if (isConsolidatedDashboard) return false;
    return infoResponse?.product_accounts?.length > 1;
  }, [infoResponse]);

  useEffect(() => {
    if (!isConsolidatedDashboardEnabled) return;
    let mids = getSelectedConsolidatedMids();
    if (mids?.length > 0) {
      selectedMidsRef.current = mids;
    } else {
      const data = getConsolidatedUsersDetails();
      const consolidatedMids = data.map((d) => d.identifier);
      selectedMidsRef.current = consolidatedMids;
      setSelectedConsolidatedMids(consolidatedMids);
    }
  }, []);

  useEffect(() => {
    const extraMarginClassName = 'kt-aside--fixed';
    const bodyElement = document.querySelector('body');
    const topBarElement = document.querySelector('#top-menu-bar');
    const overlayElement = document.querySelector('#open-bar-overlay');

    if (isMobile || isTablet) {
      bodyElement?.classList?.remove(extraMarginClassName);
      topBarElement?.classList.add('mobile');
    }
    else {
      setShowMSideMenu(false);      
      if (!bodyElement?.classList?.contains(extraMarginClassName))
        bodyElement?.classList?.add(extraMarginClassName);
      if (topBarElement?.classList?.contains('mobile'))
        topBarElement?.classList.remove('mobile');
      if (overlayElement)
        overlayElement.style.display = 'none';
    }
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (isVistaApplication()) {
      window.document.title = 'Vista Dashboard';
      setSideMenus(VistaSideMenu);
    }
  }, []);

  useEffect(() => {
  setIsActiveDowntime(activeDowntimeData.length > 0);
}, [activeDowntimeData]);

const fetchDowntimeData = async () => {
  await fetchDowntime();
};

useEffect(() => {
  fetchDowntimeData();

  if (isActiveDowntime) {
    intervalIdRef.current = setInterval(fetchDowntimeData, 2 * 60 * 1000); // 2 minutes in milliseconds
  }

  return () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
  };
}, [isActiveDowntime]);

  const updateFinalSection = (
    sections,
    merchantInfo,
    userScopes,
    forceShowTabs
  ) => {
    const { activeMenu, finalMenuItem } = getFinalSection(
      sections,
      merchantInfo,
      userScopes,
      forceShowTabs,
      isConsolidatedDashboard,
      merchantParams,
      isMobile,
      isTablet,
    );
    setSideMenus({ sections: finalMenuItem });
    setSelectedMenu(activeMenu);
  };

  useEffect(() => {
    if (merchantInfo && userScopes) {
      updateSwitcherMenuWithEvent(
        DASHBOAR_SWITCHER_MENU,
        merchantInfo.status?.onboardingCompleted
      );
      updateFinalSection(
        sideMenus.sections,
        merchantInfo,
        userScopes,
        forceShowTabs
      );
    }
  }, [window.location.pathname, merchantInfo, userScopes, forceShowTabs]);

  useEffect(() => {
    if (merchantInfo && activeApp) {
      const menus = getMenus({
        appName: activeApp,
        merchantInfo,
        isMobile: isMobile || isTablet,
        showSwitchMid,
        merchantParams
      });
      setTopbarMenus(menus);
    }
  }, [merchantInfo, activeApp]);

  useEffect(() => {
    function repoChange() {
      const loadedRepos = getMountedApps();
      if (loadedRepos?.length > 1) {
        const appName = loadedRepos[1];
        setActiveApp(appName);
      }
    }
    window.addEventListener('single-spa:app-change', repoChange);
    return () =>
      window.removeEventListener('single-spa:app-change', repoChange);
  }, []);

  useEffect(() => {
    const overlayEl = document.getElementById('open-bar-overlay');
    overlayEl.addEventListener('click', toggleSideBar);
    return () => overlayEl.removeEventListener('click', toggleSideBar);
  }, []);

  const DASHBOAR_SWITCHER_MENU = useMemo(
    () => ({
      dropdownOptions: [
        {
          label: 'Payments',
          icon: 'payout',
          labelProps: {
            className: 'tw-m-0',
          },
          selected: true,
          handleClick: () =>
            navigateToUrl(`${process.env.DASHBOARD_HOST}/business/`),
        },
        {
          icon: 'report',
          label: 'PayU Insights',
          selected: false,
          handleClick: () =>
            window.open(`${process.env.DASHBOARD_HOST}/business/lookup`),
        },
        ...(isAdmin
          ? [
              {
                label: 'FlexiCash',
                icon: 'flex-cash',
                selected: false,
                handleClick: (e) => {
                  setOpenPopup(true);
                },
              },
            ]
          : []),
      ],
    }),
    [isAdmin]
  );

  const onEnvChange = (selectedSession) => {
    if (topbarMenus?.isSessionSwticherEnabled) {
      setMerchantSession(selectedSession);
      toggleMerchantSession(selectedSession);
    }
    if (topbarMenus?.canEnableSessionSwitcher) {
      setShowSwitchModeModal(true);
    }
  };
  const fetchMerchantDetails = async () => {
    try {
      const merchantResponse = await fetchMerchantInfo();
      if (merchantResponse?.status == 200 || merchantResponse?.status == 202) {
        let info = migrateToV1(JSON.parse(JSON.stringify(merchantResponse)));
        setMerchantInfo(info?.data?.result);
        if (!isVistaApplication()) {
          const isFeatureAsideMenus = getFlagValueFromAttachConfig(
            info?.data?.result?.attachedConfigs || []
          );

          if (isFeatureAsideMenus) {
            // enter for few merchants to show vista neo-banking
            if (info?.data?.result?.nbEligible) {
              const featureMenus = getFeatureSideMenuSections(
                FeatureSideMenu.sections || []
              );
              setSideMenus({ sections: featureMenus });
              setIsLoading(false);
            } else {
              setSideMenus(FeatureSideMenu);
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      }
    } catch (err) {
      console.log('fetchMerchantDetails api failed: ', err);
    }
  };

  useEffect(() => {
    // Custom Show : Offers & Promotions Tab
    isOfferMerchant({
      source: 'one-dash__offerdashboard',
      pageType: 'ftu',
    })
      .then((response) => {
        if (response?.data?.data?.cohortId === 1) {
          setForceShowTabs([...forceShowTabs, 'offerEngineEnabled']);
        }
      })
      .catch((err) => {
        console.log('Merchant Offer Status API failed: ', err);
      });
  }, []);

  const fetchToken = useCallback(async () => {
    try {
      const response = await fetchTokenInfo();
      if (response?.status == 200 || response?.status == 202) {
        const userScopeList = getUserScopes(
          response.data?.product_accounts,
          merchantMID
        );
        setUserScopes(userScopeList);
        setUuid(response.data?.user?.uuid);
        setUserEmail(response.data?.user?.email);
        setIsAdmin(isAdminUser(response.data?.product_accounts, merchantMID));
        setInfoResponse(response.data);
        fetchMerchantDetails();
        try {
          const cleverTapId = isTestMode
            ? process.env.CLEVERTAP_TEST_ID
            : process.env.CLEVERTAP_ID;
          CleverTapHandler.initialize(cleverTapId);
        } catch (e) {
          console.log(e);
        }
      } else {
        console.log('fetchTokenInfo failure', response.data.message);
      }
    } catch (err) {
      console.log('fetchTokenInfo api failed: ', err);
    }
  }, []);

  const getMerchantParams = useCallback(async () => {
    try {
      const response = await fetchMerchantParams();
      if (response?.status == 200 || response?.status == 202) {
        if(response?.data?.status === 0) {
          setMerchantParams(parseForFlags(response?.data?.result));
        }
      }
    } catch(err) {
      console.log("fetch Merchant Params api failed: ", err);
    }
  }, [])

  useEffect(() => {
    fetchToken();
    getMerchantParams();
  }, []);

  const toggleSideBar = () => {
    setShowMSideMenu((s) => {
      document.getElementById('open-bar-overlay').style.display = s
        ? 'none'
        : 'block';
      return !s;
    });
  };

  const handleClick = (props) => {
    const { item } = props;
    if (isMobile || isTablet) {
      toggleSideBar();
    }
    if (!item) return;
    if (item?.newTab) {
      if (item?.vistaRedirect) {
        history.push(item?.path);
        window.open(appUrls?.VISTA_APPLICATION, '_blank');
      } else {
        event('Dashboard CTA clicked', {
          'CTA name': item?.text,
          'CTA page': `${getPageName(
            window?.location?.pathname?.split('/')?.pop(),
            merchantInfo && merchantInfo?.status?.onboardingCompleted
          )}`,
          'Section name': 'Nav bar',
          'CTA type': 'Action',
        });
        window.open(item?.path, '_blank');
      }
    } else {
      if (item?.title == 'Banking Dashboard') {
        event('Dashboard CTA clicked', {
          'CTA name': 'Banking Dashboard',
          'CTA page': 'Navigation Bar',
          'Section name': isVistaApplication() ? 'Vista' : 'OneDash',
          'CTA type': 'Action',
        });
      }
      if (item?.text == 'FlexiCash') {
        setOpenPopup(isAdmin);
      }
      event('Dashboard CTA clicked', {
        'CTA name': item?.text,
        'CTA page': `${getPageName(
          window?.location?.pathname?.split('/')?.pop(),
          merchantInfo && merchantInfo?.status?.onboardingCompleted
        )}`,
        'Section name': 'Nav bar',
        'CTA type': 'Action',
      });
      setSelectedMenu(item?.text || '');
      history.push(item?.path);
    }
  };
  if (!DASHBOAR_SWITCHER_MENU.onClick) {
    DASHBOAR_SWITCHER_MENU.onClick = (option) => {
      toggleSideBar();
      if (option?.url) {
        navigateToUrl(option?.url);
      }
    };
  }

  const HEADER_CTAS = useMemo(() => {
    const CTAS = [
      {
        ctaType: 'icon',
        type: 'signal-tower',
        size: 'm',
        color: '#ffffff',
        iconBorder: {
          color: 'green',
        },
        highlight: {
          show: isActiveDowntime,
          color: 'red',
        },
        onClick: () => {
          event('Dashboard CTA clicked', {
            'Event name': 'Dashboard CTA clicked',
            'CTA page': `${getPageName(
              window?.location?.pathname?.split('/')?.pop(),
              merchantInfo && merchantInfo?.status?.onboardingCompleted
            )}`,
            'CTA name': 'Downtime Notifications',
            'Section name': 'Header',
            'CTA type': 'View',
          });
          const customE = new Event('downtimeNotification');
          // Dispatch the event.
          window.dispatchEvent(customE);
        },
        // onClick: () => { SF_BOT?.startChat() },
      },
      {
        ctaType: 'icon',
        type: 'support',
        size: 'm',
        color: '#ffffff',
        onClick: () => {
          event('Dashboard CTA clicked', {
            'Event name': 'Dashboard CTA clicked',
            'CTA page': `${getPageName(
              window?.location?.pathname?.split('/')?.pop(),
              merchantInfo && merchantInfo?.status?.onboardingCompleted
            )}`,
            'CTA name': 'Help',
            'Section name': 'Header',
            'CTA type': 'View',
          });
          window.open(process.env.CARE_HOST);
        },
        // onClick: () => { SF_BOT?.startChat() },
      },
    ];
    if (isConsolidatedDashboard) {
      CTAS.unshift({
        ctaType: "dropdown",
        chipColor: "green",
        className: "payu_mid_switcher",
        optionsClassName: "payu_mid_switcher_option",
        dropdownOptions: getMidSwitcherOptions(merchantParams),
        isMultiSelect: true,
        searchEnabled: true,
        onSearch: function noRefCheck() {},
        onSelect: function noRefCheck() {},
        placeholder: "Select Mid's",
        cancelOnOutsideClick: true,
        tooltipPlacement: 'top',
        actionButtons: [
          {
            onClick: function noRefCheck() {},
            size: 'large',
            text: 'Cancel',
            variant: 'SECONDARY',
          },
          {
            onClick: (options) => {
              if (options.length) {
                const selectedMids = options.map((o) => o.value);
                selectedMidsRef.current = selectedMids;
                setSelectedConsolidatedMids(selectedMids);
                window.location.reload();
              }
            },
            size: 'large',
            text: 'Apply',
            variant: 'PRIMARY',
            disableOnNoSelection: true,
            applyBtn: true,
          },
        ],
      });
    }
    return CTAS;
  }, [merchantParams]);

  return (
    <>
      <SingleSpaContext.Consumer>
        {({ globals }) => {
          const { MOBILE_VIEW_PROPS = {}, SF_BOT = {}, THEME_PROPS = {} } = globals;
          return (
            <>
              {MOBILE_VIEW_PROPS.TOPBAR_VISIBLE && (
                <Portal parentDivId='top-menu-bar'>
                  <TopHeader
                    className='payu_top_bar'
                    onHamburgerMenuClick={toggleSideBar}
                    pageName={selectedMenu}
                    onLogoClick={() =>
                      navigateToUrl(process.env.DASHBOARD_HOST)
                    }
                    headerCTAs={HEADER_CTAS}
                    userMenu={{
                      userLogo: merchantInfo?.logo,
                      dropdownOptions: topbarMenus?.userMenu?.menuItems || [],
                      onClick: (option) => {
                        if (
                          option.identifier ===
                          MENU_ITEM_IDENTIFIERS.SWITCH_TO_BIZ_DASHBOARD
                        ) {
                          event('Dashboard CTA clicked', {
                            'Event name': 'Dashboard CTA clicked',
                            'CTA page': `${getPageName(
                              window?.location?.pathname?.split('/')?.pop(),
                              merchantInfo &&
                                merchantInfo?.status?.onboardingCompleted
                            )}`,
                            'CTA name': 'Switch to PayUBiz',
                            'Section name': 'Header',
                            'CTA type': 'Click',
                          });
                          let wnd = window.open('about:blank', '', '_blank');
                          wnd.document.write(
                            getLoginToBizForm(merchantMID, authToken)
                          );
                          return;
                        }
                        if (
                          option.identifier ===
                          MENU_ITEM_IDENTIFIERS.SF_CHAT_BUTTON
                        ) {
                          SF_BOT?.startChat();
                          return;
                        }
                        if (
                          option.identifier ===
                          MENU_ITEM_IDENTIFIERS.USER_LOGOUT
                        ) {
                          event('Dashboard CTA clicked', {
                            'Event name': 'Dashboard CTA clicked',
                            'CTA page': `${getPageName(
                              window?.location?.pathname?.split('/')?.pop(),
                              merchantInfo &&
                                merchantInfo?.status?.onboardingCompleted
                            )}`,
                            'CTA name': 'Sign out',
                            'Section name': 'Account Dropdown',
                            'CTA type': 'Click',
                          });
                          ls.remove('currentSession');
                          ls.remove('new_login');
                          ls.remove('button_filter');
                          ls.remove('button_date_range');
                          ls.remove('consolidated_product_accounts');
                          ls.set('logoutAllTabs', true);
                          window.localStorage.removeItem('onboardingNudge');
                          navigateToUrl(logoutUrl);
                          return;
                        }
                        event('Dashboard CTA clicked', {
                          'Event name': 'Dashboard CTA clicked',
                          'CTA page': `${getPageName(
                            window?.location?.pathname?.split('/')?.pop(),
                            merchantInfo &&
                              merchantInfo?.status?.onboardingCompleted
                          )}`,
                          'CTA name': `${option?.label}`,
                          'Section name': 'Header',
                          'CTA type': 'Click',
                        });
                        navigateToUrl(option?.path || '#');
                      },
                    }}
                    sandboxSwitcher={{
                      enable:
                        topbarMenus?.isSessionSwticherEnabled ||
                        topbarMenus?.canEnableSessionSwitcher,
                      onModeChange: onEnvChange,
                      selected: merchantSession,
                    }}
                    logoAttributes={{
                      logoUrl: THEME_PROPS?.whiteLableMerchant ? THEME_PROPS?.bankLogo || null : null
                    }}
                    selectorMenu={DASHBOAR_SWITCHER_MENU}
                  />
                </Portal>
              )}
              <div
                className={clsx('oneui-left-side-bar tour-step4', {
                  'm-side-bar mobile': isMobile || isTablet,
                  'show-side-bar': showMSideMenu,
                })}
              >
                {showSwitchModeModal && (
                  <SwitchModes
                    setShowSwitchModeModal={setShowSwitchModeModal}
                    merchantInfo={merchantInfo}
                    setMerchantSession={setMerchantSession}
                  />
                )}
                <SideNav
                  isLoading={isLoading}
                  selectorMenu={DASHBOAR_SWITCHER_MENU}
                  onClick={handleClick}
                  sections={sideMenus.sections}
                  customLogo={{
                    logoUrl: THEME_PROPS?.whiteLableMerchant ? THEME_PROPS?.bankLogo || null : null
                  }}
                />
              </div>
              {openPopup && (
                <PayuFinModal
                  open={openPopup}
                  onClose={() => setOpenPopup(false)}
                  uuid={uuid}
                  userEmail={userEmail}
                />
              )}
            </>
          );
        }}
      </SingleSpaContext.Consumer>
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchDowntime: () => {
      dispatch(downTimeListActions.fetchData());
    },
  };
};

const mapStateToProps = (state) => {
  return {
    downtimeList: downTimeListSelectors.getDowntimeList(state),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(App);
